import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const MIN_LENGTH = 8;

const PasswordChangeCard = () => {
  const { profile } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.email) {
      toast.error("No se pudo identificar tu cuenta");
      return;
    }
    if (newPassword.length < MIN_LENGTH) {
      toast.error("Contraseña demasiado corta", { description: `Debe tener al menos ${MIN_LENGTH} caracteres.` });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("No coinciden", { description: "La nueva contraseña y su confirmación deben ser iguales." });
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("Sin cambios", { description: "La nueva contraseña debe ser distinta a la actual." });
      return;
    }

    setIsSubmitting(true);

    // Verify the current password by attempting to re-authenticate with it.
    // Supabase's updateUser doesn't require the old password, so we check it explicitly.
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: profile.email,
      password: currentPassword,
    });
    if (verifyError) {
      setIsSubmitting(false);
      const raw = (verifyError.message || "").toLowerCase();
      const description = raw.includes("invalid") || raw.includes("credentials")
        ? "La contraseña actual no es correcta."
        : verifyError.message;
      toast.error("No se pudo verificar", { description });
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) {
      setIsSubmitting(false);
      const raw = (updateError.message || "").toLowerCase();
      let description = updateError.message;
      if (raw.includes("weak") || raw.includes("pwned") || raw.includes("known to be")) {
        description = "La contraseña es demasiado común o ha sido filtrada en brechas de seguridad. Elige otra más segura.";
      } else if (raw.includes("should be at least") || raw.includes("at least")) {
        description = `La contraseña es demasiado corta. Debe tener al menos ${MIN_LENGTH} caracteres.`;
      } else if (raw.includes("same as the old") || raw.includes("different from the old")) {
        description = "La nueva contraseña debe ser distinta a la anterior.";
      } else if (raw.includes("rate limit")) {
        description = "Demasiados intentos. Espera unos segundos e inténtalo de nuevo.";
      }
      toast.error("No se pudo actualizar", { description });
      return;
    }

    setIsSubmitting(false);
    reset();
    toast.success("Contraseña actualizada", { description: "Tu nueva contraseña ya está activa." });
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-primary" />
          Seguridad
        </CardTitle>
        <CardDescription>Cambia tu contraseña de acceso a la plataforma.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Contraseña actual</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="current-password"
                type={showCurrent ? "text" : "password"}
                placeholder="••••••••"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pl-10 pr-10 bg-background/50"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showCurrent ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">Nueva contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10 pr-10 bg-background/50"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showNew ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Mínimo {MIN_LENGTH} caracteres.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirmar nueva contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 pr-10 bg-background/50"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full gap-2"
            disabled={isSubmitting || !currentPassword || !newPassword || !confirmPassword}
          >
            <KeyRound className="w-4 h-4" />
            {isSubmitting ? "Actualizando..." : "Actualizar contraseña"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PasswordChangeCard;
